import React, { useState } from "react";
import Icon from "@mdi/react";
import { mdiArrowDownDropCircleOutline, mdiArrowRightDropCircleOutline } from "@mdi/js";
import { Table, Popup, Grid, TableRow, Placeholder } from "semantic-ui-react";
import { format } from "date-fns";
import "./customSupplierRequest.scss";
import { isArray } from "lodash";
import { MappingInteractionStatus, SupplierRequestCol } from "../../config/constants";
import { warningMessage } from "../../helpers/ErrorHandler";
import PaginationView from "../pagination/Pagination";
import ReminderIcon from "../../assets/commonIcon/ReminderIcon";
import { CommonTable } from "../commonTable/CommonTable";
import { InputText } from "../InputText/InputText";
import { useForm } from "react-hook-form";
import DebounceSearch from "../debounceSearch/DebounceSearch";

interface CustomSupplierRequestProps {
  data: any;
  pageNumber: number;
  pageLimit: number;
  dataTotal: number;
  onReminder: (arg: any) => void;
  handlePagination: (arg: any) => void;
  handlePageSize: (arg: any) => void;
  setSearchKey: (arg: string) => void;
  searchKey: string;
  refetch?: () => void;
  isRecipeListLoading: boolean;
}

const CustomSupplierRequest: React.FC<CustomSupplierRequestProps> = ({
  data,
  pageNumber,
  pageLimit,
  dataTotal,
  onReminder,
  handlePagination,
  handlePageSize,
  setSearchKey,
  searchKey,
  refetch,
  isRecipeListLoading = false,
}) => {
  const [expandedRows, setExpandedRows] = useState<number[]>([]);

  const { register, watch } = useForm();

  const handleRowClick = (rowId: number) => {
    const currentExpandedRows = expandedRows;
    const isRowCurrentlyExpanded = currentExpandedRows.includes(rowId);

    const newExpandedRows = isRowCurrentlyExpanded
      ? currentExpandedRows.filter(id => id !== rowId)
      : currentExpandedRows.concat(rowId);
    setExpandedRows(newExpandedRows);
  };

  const renderItemCaret = (rowId: number) => {
    const currentExpandedRows = expandedRows;
    const isRowCurrentlyExpanded = currentExpandedRows.includes(rowId);

    if (isRowCurrentlyExpanded) {
      return mdiArrowDownDropCircleOutline;
    } else {
      return mdiArrowRightDropCircleOutline;
    }
  };

  const loadTableData = () => {
    if (isRecipeListLoading) {
      return (
        <>
          {[...Array(5)].map((_, i) => (
            <TableRow key={i}>
              <Table.Cell colSpan={7}>
                <Placeholder>
                  <Placeholder.Line length="full" />
                  <Placeholder.Line length="very long" />
                </Placeholder>
              </Table.Cell>
            </TableRow>
          ))}
        </>
      );
    }
    if (!data || data.length === 0) {
      return (
        <TableRow>
          <Table.Cell colSpan={7} style={{ textAlign: "center" }}>
            No Data Found
          </Table.Cell>
        </TableRow>
      );
    }
    return data?.map((item: any, index: any) => {
      const defaultStatus: number = item?.status?.[0]?.[0] || 1;
      const defaultStatusTime: string = format(
        new Date(item?.status?.[0]?.[1] || new Date()),
        "dd/MM/yyyy"
      );
      const checkStatusIsAvailable = isArray(item.status) && item.status.length > 1;

      const itemRows = [
        <Table.Row className="tbleR" key={index}>
          <Table.Cell
            onClick={() => {
              checkStatusIsAvailable
                ? handleRowClick(index)
                : warningMessage("Still not available more status");
            }}
          >
            <Icon
              className="table-icon"
              color="var(--tableEditIcon)"
              path={renderItemCaret(index)}
              size={1.2}
            />
          </Table.Cell>
          <Table.Cell>{item?.supplier?.supplier_name}</Table.Cell>
          <Table.Cell>{item?.ingredients?.ingredient_name}</Table.Cell>
          <Table.Cell>{MappingInteractionStatus[defaultStatus]}</Table.Cell>
          <Table.Cell>{defaultStatusTime}</Table.Cell>
          <Table.Cell className="on_reminder_view">
            <Popup
              content={"Send Reminder"}
              trigger={
                <ReminderIcon
                  onClick={() => {
                    onReminder(item);
                  }}
                />
              }
            />
          </Table.Cell>
        </Table.Row>
      ];

      if (isArray(item.status) && item.status.length > 1 && expandedRows.includes(index)) {
        item.status.slice(1).map((data: any, index: any) => {
          const statusNo: any = data[0] || 1;
          const statusData = format(new Date(data[1] || new Date()), "dd/MM/yyyy");

          return itemRows.push(
            <Table.Row key={"row-expanded-" + index}>
              <Table.Cell></Table.Cell>
              <Table.Cell>{item?.supplier?.supplier_name}</Table.Cell>
              <Table.Cell>{item?.ingredients?.ingredient_name}</Table.Cell>
              <Table.Cell>{MappingInteractionStatus[statusNo]}</Table.Cell>
              <Table.Cell>{statusData}</Table.Cell>
            </Table.Row>
          );
        });
      }
      return itemRows;
    });
  };

  return (
    <>
      <Grid>
        <Grid.Column computer={4}>
          <DebounceSearch
            refetch={refetch}
            placeholder="Search by Supplier name or Ingredient name"
            setSearchKey={setSearchKey}
            searchKey={searchKey}
          />
        </Grid.Column>
        <Grid.Column computer={16} tablet={16} mobile={16}>
          <CommonTable singleLine tableHeaderData={SupplierRequestCol}>
            {loadTableData()}
          </CommonTable>
          <PaginationView
            changePageSize={handlePageSize}
            pageSize={pageLimit}
            totalPages={Math.ceil(dataTotal / pageLimit)}
            currentPageNo={pageNumber}
            currentSelectPage={handlePagination}
          />
        </Grid.Column>
      </Grid>
    </>
  );
};

export default CustomSupplierRequest;
